import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useFamilyDetails } from '@/hooks/useFamilyDetails';
import { Family, Parent, Student } from '@/types/models';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { StandardPageHeader } from '@/components/layout/StandardPageHeader';
import { 
  Users, 
  User, 
  Mail, 
  Phone, 
  Video, 
  UserCheck, 
  AlertCircle, 
  ArrowLeft,
  Plus,
  Edit,
  Calendar
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import AddParentDialog from '../AddParentDialog';
import AddStudentDialog from '../AddStudentDialog';
import EditParentDialog from '../EditParentDialog';

const HiveShareFamilyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { organizationId } = useAuth();
  
  // Ensure familyId is a string (fix for [object Object] error)
  const familyId = typeof id === 'string' ? id : String(id) || '';
  
  // Dialog state
  const [isAddParentDialogOpen, setIsAddParentDialogOpen] = useState(false);
  const [isAddStudentDialogOpen, setIsAddStudentDialogOpen] = useState(false);
  const [editingParent, setEditingParent] = useState<Parent | null>(null);
  const [isEditParentDialogOpen, setIsEditParentDialogOpen] = useState(false);

  // Get family details
  const {
    family,
    parents,
    students,
    loading
  } = useFamilyDetails(familyId);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getVideoAccessStatus = () => {
    const hasParentWithEmail = parents.some(parent => parent.email && parent.email.trim());
    
    if (hasParentWithEmail) {
      return {
        label: 'Can Access Videos',
        variant: 'default' as const,
        color: 'text-green-600',
        bg: 'bg-green-50',
        icon: Video
      };
    } else {
      return {
        label: 'No Video Access',
        variant: 'secondary' as const,
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        icon: AlertCircle
      };
    }
  };

  const handleEditParent = (parent: Parent) => {
    setEditingParent(parent);
    setIsEditParentDialogOpen(true);
  };

  const handleDialogSuccess = () => {
    // Refresh the page to reload data
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Skeleton className="h-8 w-8" />
          <Skeleton className="h-8 w-32" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-4">
                <Skeleton className="h-16 w-16 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-4 w-24" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!loading && !family) {
    return (
      <div className="space-y-6">
        <StandardPageHeader
          title="Family Not Found"
          subtitle="The requested family could not be found"
          icon={Users}
        />
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-red-600 mb-4">
              Family not found
            </p>
            <Button onClick={() => navigate('/admin/families')} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Families
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusInfo = getVideoAccessStatus();
  const StatusIcon = statusInfo.icon;
  const totalMembers = parents.length + students.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/admin/families')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{family.name}</h1>
            <p className="text-gray-500 mt-1">
              Family details and video access
            </p>
          </div>
        </div>
        <Badge variant={statusInfo.variant} className={`${statusInfo.color} ${statusInfo.bg}`}>
          <StatusIcon className="h-4 w-4 mr-2" />
          {statusInfo.label}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Family Info */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              Family Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Family Basic Info */}
            <div className="flex items-center space-x-4">
              <Avatar className="h-16 w-16 bg-blue-100">
                <AvatarFallback className="text-blue-700 text-lg font-semibold">
                  {getInitials(family.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900">{family.name}</h2>
                <div className="flex items-center text-gray-500 mt-1">
                  <Users className="h-4 w-4 mr-1" />
                  <span>{totalMembers} member{totalMembers !== 1 ? 's' : ''}</span>
                </div>
                {family.notes && (
                  <p className="text-sm text-gray-600 mt-2">{family.notes}</p>
                )}
              </div>
            </div>

            <Separator />

            {/* Parents Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Parents ({parents.length})
                </h3>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setIsAddParentDialogOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Parent
                </Button>
              </div>
              
              {parents.length === 0 ? (
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                  <User className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">No parents added yet</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Add a parent with email to enable video access
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {parents.map((parent) => (
                    <Card key={parent.id} className="border-l-4 border-l-blue-500">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-medium text-gray-900">
                                {parent.name || 'Unnamed Parent'}
                              </h4>
                              {parent.is_primary && (
                                <Badge variant="secondary" className="text-xs">
                                  Primary
                                </Badge>
                              )}
                            </div>
                            <div className="space-y-1 text-sm text-gray-600">
                              {parent.email && (
                                <div className="flex items-center gap-2">
                                  <Mail className="h-4 w-4 text-gray-400" />
                                  <span>{parent.email}</span>
                                </div>
                              )}
                              {parent.phone && (
                                <div className="flex items-center gap-2">
                                  <Phone className="h-4 w-4 text-gray-400" />
                                  <span>{parent.phone}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleEditParent(parent)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            <Separator />

            {/* Students Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Children ({students.length})
                </h3>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setIsAddStudentDialogOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Child
                </Button>
              </div>
              
              {students.length === 0 ? (
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                  <Users className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">No children added yet</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Add children to identify them in videos
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {students.map((student) => (
                    <Card key={student.id} className="border-l-4 border-l-green-500">
                      <CardContent className="p-4">
                        <h4 className="font-medium text-gray-900 mb-2">
                          {student.name}
                        </h4>
                        <div className="space-y-1 text-sm text-gray-600">
                          {student.date_of_birth && (
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <span>
                                Born {new Date(student.date_of_birth).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                          {student.grade && (
                            <div className="text-gray-500">
                              Grade: {student.grade}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Video Access Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Video className="h-5 w-5 text-blue-600" />
              Video Access
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className={`p-4 rounded-lg ${statusInfo.bg} border border-current/20`}>
              <div className="flex items-center gap-2 mb-2">
                <StatusIcon className={`h-5 w-5 ${statusInfo.color}`} />
                <span className={`font-medium ${statusInfo.color}`}>
                  {statusInfo.label}
                </span>
              </div>
              <p className="text-sm text-gray-600">
                {parents.some(p => p.email) 
                  ? 'This family can receive video invitations and access shared videos.'
                  : 'Add a parent email address to enable video sharing for this family.'
                }
              </p>
            </div>

            {/* Setup Instructions */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Setup Checklist</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  {parents.length > 0 ? (
                    <UserCheck className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-gray-400" />
                  )}
                  <span className={parents.length > 0 ? 'text-green-600' : 'text-gray-500'}>
                    Add parent contact
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  {parents.some(p => p.email) ? (
                    <UserCheck className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-gray-400" />
                  )}
                  <span className={parents.some(p => p.email) ? 'text-green-600' : 'text-gray-500'}>
                    Add parent email
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  {students.length > 0 ? (
                    <UserCheck className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-gray-400" />
                  )}
                  <span className={students.length > 0 ? 'text-green-600' : 'text-gray-500'}>
                    Add children (optional)
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dialogs */}
      <AddParentDialog
        open={isAddParentDialogOpen}
        onOpenChange={setIsAddParentDialogOpen}
        familyId={familyId}
        organizationId={organizationId!}
        onSuccess={handleDialogSuccess}
      />

      <AddStudentDialog
        open={isAddStudentDialogOpen}
        onOpenChange={setIsAddStudentDialogOpen}
        familyId={familyId}
        organizationId={organizationId!}
        onSuccess={handleDialogSuccess}
      />

      {editingParent && (
        <EditParentDialog
          open={isEditParentDialogOpen}
          onOpenChange={setIsEditParentDialogOpen}
          parent={editingParent}
          onSuccess={handleDialogSuccess}
        />
      )}
    </div>
  );
};

export default HiveShareFamilyDetail;